import { useEffect, useState } from "react";
import { useProfiles } from "../settings/profileStore.js";
import { NavLink } from "react-router";
import { cn } from "@hybrid-iptv/ui";
import { dvrAvailable } from "../dvr/dvr.js";
import { brand } from "../brand.js";
import { askToLeaveApp, nativeBackAvailable } from "../api/nativeBack.js";

const ITEMS = [
  { to: "/", label: "Home", icon: "M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5" },
  { to: "/live", label: "Live TV", icon: "m10 9 5 3-5 3zM3 5h18v14H3z", live: true },
  { to: "/movies", label: "Movies", icon: "M7 5v14M17 5v14M3 9h4M3 15h4M17 9h4M17 15h4" },
  { to: "/series", label: "Series", icon: "M2 7h20v13H2zM7 7 9 4h6l2 3" },
  { to: "/mylist", label: "My List", icon: "m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.3l6.1-.7z" },
  // A clock wound backwards: the archive, not a recording you scheduled.
  { to: "/catchup", label: "Catch-up", icon: "M3 12a9 9 0 1 0 3-6.7M3 4v5h5M12 7v5l3.5 2" },
  { to: "/recordings", label: "Recordings", icon: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z", dvrOnly: true },
  { to: "/search", label: "Search", icon: "m21 21-4.3-4.3M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14" },
  // Watch together (docs/feature-plan.md §12a): two people side by side.
  { to: "/social", label: "Social", icon: "M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2 20a7 7 0 0 1 14 0M16 4.1a3.5 3.5 0 0 1 0 6.8M18 13.5a7 7 0 0 1 4 6.5" },
  { to: "/settings", label: "Settings", icon: "M12 2v4M12 18v4M2 12h4M18 12h4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" },
];

const STORAGE_KEY = "slique-nav-collapsed";
/** Matches the responsive breakpoint in app.css. */
const NARROW_PX = 720;

/**
 * Site-wide collapsible navigation rail. Collapsed = icon-only rail (~64px)
 * so the guide/movies/series surfaces get more width. The state is mirrored
 * on <body class="nav-collapsed"> so the fixed player dock (rendered outside
 * the .app tree) can shift left in sync — see app.css.
 */
export function NavRail() {
  // The active profile, so the rail shows who is watching and doubles as the
  // way into the switcher.
  const activeProfile = useProfiles((s) => s.profiles.find((p) => p.id === s.activeProfileId));
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // Nothing chosen yet: closed on a phone, open where there is room.
      if (stored === null) return window.innerWidth <= NARROW_PX;
      return stored === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      // Storage unavailable — the rail still toggles in-memory for this session
    }
    document.body.classList.toggle("nav-collapsed", collapsed);
  }, [collapsed]);

  return (
    <nav
      className={cn("navrail", collapsed && "navrail--collapsed")}
      aria-label="Primary"
      // The rail scrolls, and the TV WebView can focus a scrolling box itself:
      // focus then sat on the rail as a whole, nothing highlighted, and the
      // next OK did nothing (projector, 2026-09-27). Focus that lands on the
      // container moves to the current screen's item.
      onFocus={(event) => {
        // The top of the rail stays reachable (owner, 2026-10-05): walking down
        // the rail scrolls its top — the brand, this arrow, Home — off screen,
        // and coming back up only scrolled as far as each item, so the arrow
        // and, on Live TV, the categories above were hard to reach. Arriving on
        // the arrow or the first item puts the rail back at its top.
        const rail = event.currentTarget;
        const target = event.target instanceof HTMLElement ? event.target : null;
        if (target && (target.matches(".navrail__toggle") || target === rail.querySelector(".navrail__item"))) rail.scrollTop = 0;
        if (event.target !== event.currentTarget) return;
        const item =
          event.currentTarget.querySelector<HTMLElement>(".navrail__item--active") ??
          event.currentTarget.querySelector<HTMLElement>("a[href], button");
        item?.focus();
      }}
    >
      <div className="navrail__brand">
        <img className="navrail__mark" src={brand().icon} alt={brand().appName} />
        <span className="navrail__name">{brand().appName}</span>
      </div>
      <button
        type="button"
        className="navrail__toggle"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        title={collapsed ? "Expand navigation" : "Collapse navigation"}
      >
        <svg viewBox="0 0 24 24" aria-hidden>
          <path d={collapsed ? "m8 5 7 7-7 7" : "m16 5-7 7 7 7"} />
        </svg>
      </button>
      {ITEMS.filter((item) => !item.dvrOnly || dvrAvailable()).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          title={item.label}
          className={({ isActive }) => cn("navrail__item", isActive && "navrail__item--active")}
        >
          <svg viewBox="0 0 24 24" aria-hidden>
            <path d={item.icon} />
          </svg>
          <span>{item.label}</span>
          {item.live && <span className="navrail__live">LIVE</span>}
        </NavLink>
      ))}
      <div className="navrail__spacer" />
      <NavLink to="/settings/profiles" className="navrail__profile" title="Switch profile">
        <span className="navrail__avatar">{activeProfile?.avatar ?? "😀"}</span>
        <span>{activeProfile?.name ?? "Me"}</span>
      </NavLink>
      {/* Last in the rail, reached from anywhere without walking Back to Home.
          It asks first (ExitConfirm), starting on Stay. */}
      {nativeBackAvailable() && (
        <button type="button" className="navrail__item navrail__exit" onClick={askToLeaveApp} title="Exit">
          <svg viewBox="0 0 24 24" aria-hidden>
            <path d="M12 3v9M6.3 6.3a8 8 0 1 0 11.4 0" />
          </svg>
          <span>Exit</span>
        </button>
      )}
    </nav>
  );
}
